import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { projectsProjectReferenceAccessHoldCapability } from '@crest/module-projects/contracts/project-reference';
import { platformPublicRequestGuardCapability } from '@crest/platform-contracts/capabilities';
import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, platformPortalHostCapability, } from '@crest/platform-contracts/hosts';
import { projectsPortalProjectSummaryCapability } from '@crest/module-projects/contracts/portal-project-summary';
import { customerPortalProjectAccessLinksCapability, customerPortalProjectHostCapability, } from './contracts/customer-portal-project-access.contract.js';
import { customerPortalInsuranceClaimsIntakeAcceptanceCapability, customerPortalProjectIntakeCapability, customerPortalProjectIntakeReadGrant, customerPortalProjectIntakeWriteGrant, customerPortalProjectsIntakeAcceptanceCapability, customerPortalRelationshipsIntakeAcceptanceCapability, } from './contracts/customer-portal-project-intake.contract.js';
import { customerPortalProjectIntakeAssetCapability } from './contracts/customer-portal-project-intake-asset.contract.js';
import { platformStoragePublicResourceDelegationCapability } from '@crest/platform-contracts/storage';
export const customerPortalModuleManifest = defineCrestModule({
    id: 'customer-portal', version: '1.0.0', kind: 'business', required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1', 'platform.module-access.v1',
        platformPublicRequestGuardCapability,
        platformOptionalActionProviderHostCapability,
        platformOptionalFactProviderHostCapability, platformPortalHostCapability,
        projectsPortalProjectSummaryCapability, 'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        projectsProjectReferenceAccessHoldCapability,
        customerPortalProjectsIntakeAcceptanceCapability,
        platformStoragePublicResourceDelegationCapability,
    ],
    optionalCapabilities: [
        customerPortalRelationshipsIntakeAcceptanceCapability,
        customerPortalInsuranceClaimsIntakeAcceptanceCapability,
    ],
    optionalCapabilityFallbacks: [{
            capability: customerPortalRelationshipsIntakeAcceptanceCapability,
            degradedMode: 'customer-portal.degraded.relationships-intake-facts-unavailable.v1',
        }, {
            capability: customerPortalInsuranceClaimsIntakeAcceptanceCapability,
            degradedMode: 'customer-portal.degraded.insurance-intake-facts-unavailable.v1',
        }],
    provides: [
        customerPortalProjectAccessLinksCapability,
        customerPortalProjectHostCapability,
        customerPortalProjectIntakeCapability,
        customerPortalProjectIntakeAssetCapability,
    ],
    permissions: [
        'customer-portal.project-links.read', 'customer-portal.project-links.manage',
        'customer-portal.project-intakes.read', 'customer-portal.project-intakes.manage',
        customerPortalProjectIntakeReadGrant, customerPortalProjectIntakeWriteGrant,
    ],
    settings: [
        'customer-portal.access.configuration',
        'customer-portal.display.project',
        'customer-portal.copy.project',
        'customer-portal.intake.configuration',
        'customer-portal.intake.display',
        'customer-portal.copy.intake',
        'customer-portal.intake-assets.configuration',
        'customer-portal.intake-assets.display',
        'customer-portal.copy.intake-assets',
    ],
    copyCatalogs: [
        'customer-portal.copy.project',
        'customer-portal.copy.intake',
        'customer-portal.copy.intake-assets',
    ],
    events: {
        publishes: [
            'customer-portal.project-link-created.v1',
            'customer-portal.project-link-revoked.v1',
            'customer-portal.project-intake-created.v1',
            'customer-portal.project-intake-link-rotated.v1',
            'customer-portal.project-intake-revoked.v1',
            'customer-portal.project-intake-submitted.v1',
            'customer-portal.project-intake-accepted.v1',
            'customer-portal.project-intake-asset-attached.v1',
            'customer-portal.project-intake-asset-deleted.v1',
            'customer-portal.project-intake-assets-transferred.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'customer-portal.routes.project-links', priority: 0 },
            { id: 'customer-portal.routes.public-project', priority: 10 },
            { id: 'customer-portal.routes.project-intakes', priority: 20 },
            { id: 'customer-portal.routes.public-project-intake', priority: 30 },
        ],
        jobs: [], subscribers: [], slots: [],
    },
    web: {
        routes: [], navigation: [],
        slots: [{
                id: 'customer-portal.web.projects.access-links',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'customer-portal.project-links.read',
                priority: 90,
            }, {
                id: 'customer-portal.web.projects.intake',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'customer-portal.project-intakes.read',
                priority: 89,
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: {
        routes: [{
                id: 'customer-portal.portal.project',
                requiredCapability: customerPortalProjectHostCapability,
                priority: 0,
            }],
        slots: [{
                id: 'customer-portal.portal.project-intake',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: customerPortalProjectIntakeReadGrant,
                priority: 10,
            }, {
                id: 'customer-portal.portal.project-intake-assets',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: customerPortalProjectIntakeReadGrant,
                priority: 11,
            }],
    },
    database: {
        schema: 'customer_portal',
        owns: [
            'customer_portal.project_access_links',
            'customer_portal.project_access_link_events',
            'customer_portal.project_access_link_commands',
            'customer_portal.portal_grants_are_valid',
            'customer_portal.resolve_project_action_resources',
            'customer_portal.resolve_project_access_link',
            'customer_portal.project_intake_requests',
            'customer_portal.project_intake_submissions',
            'customer_portal.project_intake_events',
            'customer_portal.project_intake_commands',
            'customer_portal.project_intake_public_commands',
            'customer_portal.read_project_intake_v1',
            'customer_portal.execute_project_intake_command_v1',
            'customer_portal.project_intake_assets',
            'customer_portal.project_intake_asset_commands',
            'customer_portal.read_project_intake_assets_v1',
            'customer_portal.prepare_project_intake_asset_upload_v1',
            'customer_portal.record_project_intake_asset_authorization_v1',
            'customer_portal.prepare_project_intake_asset_finalize_v1',
            'customer_portal.complete_project_intake_asset_finalize_v1',
            'customer_portal.prepare_project_intake_asset_delete_v1',
            'customer_portal.complete_project_intake_asset_delete_v1',
        ],
        readsThrough: [
            projectsPortalProjectSummaryCapability,
            projectsProjectReferenceAccessHoldCapability,
            customerPortalProjectsIntakeAcceptanceCapability,
            customerPortalRelationshipsIntakeAcceptanceCapability,
            customerPortalInsuranceClaimsIntakeAcceptanceCapability,
        ],
        migrations: [
            'customer-portal.project-access-foundation',
            'customer-portal.owner-access-scopes',
            'customer-portal.project-loss-intake',
            'customer-portal.project-intake-assets',
        ],
    },
    health: ['customer-portal.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64, jobConcurrency: 0, queueBacklogLimit: 0,
            databaseConnectionBudget: 12, workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain', clientContributions: 'remove', serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe', durableCursor: false, retentionDays: 0, backlogLimit: 0,
                warningAt: 0, reconciliationProcedure: null,
                dropSafeReason: 'This foundation publishes retained link lifecycle facts and has no inbound subscriber.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send', inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published', healthChecks: ['customer-portal.database'],
            reconciliationProcedure: null, visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove', data: 'retain', tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Portal access-link metadata and lifecycle evidence remain retained under tenant policy.',
});
//# sourceMappingURL=customer-portal.module.js.map